import { Component, input, OnInit, output } from '@angular/core';
import moment from 'moment';
import { IRouteSetupDayOfWeek, IRouteSetupHolyDay } from 'src/app/models';
import { InputType } from 'src/app/models/enums';
import { StorageService, UtilsService } from 'src/app/services';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';

@Component({
  selector: 'app-setup-card',
  templateUrl: './setup-card.component.html',
  styleUrl: './setup-card.component.scss',
  standalone: false
})
export class SetupCardComponent implements OnInit {

  readonly id = input<any>();
  readonly data = input<IRouteSetupDayOfWeek | IRouteSetupHolyDay>();
  readonly clientId = input<number | undefined>();
  readonly phoneDrivers = input<boolean>();
  readonly matchesPrevious = input<boolean>();
  readonly matchesNext = input<boolean>();
  readonly multiselectEnabled = input<boolean>();
  readonly checked = input<boolean>();
  readonly changeEventEmitter = output<any>();
  readonly checkDay = output<any>();

  public images = images;
  public TooltipPosition = TooltipPosition;
  public InputType = InputType;
  public moment = moment;

  public modifyTimes = false;
  public modifyDriver = false;
  public modifyVehicle = false;

  public timeObject = {
    height: 16,
    fontSize: 14,
    value: '',
    edit: {
      inputType: InputType.TIME
    }
  };

  public driverColors = [];
  public vehicleColors = [];

  constructor(public utilsService: UtilsService, public storageService: StorageService) {

  }

  async ngOnInit() {
    this.driverColors = JSON.parse(await this.storageService.getDriversColors()) || {};
    this.vehicleColors = JSON.parse(await this.storageService.getVehiclesColors()) || {};
  }

  getTimeObject(time: string) {
    const timeObject = {...this.timeObject};
    timeObject['value'] = time;
    return timeObject;
  }

  getTimeRange() {
    const format = (time?: Date | string) => moment(time, 'HH:mm:ss').format('HH:mm');
    return `${format(this.data()?.startTime)} - ${format(this.data()?.endTime)}`;
  }

  getDriverColor() {
    // The loaded driver page may not contain this driver, so the color comes from the id alone
    const driverId = this.data()?.driver?.id;
    return (driverId && this.driverColors[driverId]) || '#00FFFF';
  }

  assignedDriverOption() {
    const driver = this.data()?.driver;
    return driver?.id ? [{ value: driver.id, label: driver.name ?? '' }] : [];
  }

  assignedVehicleOption() {
    const vehicle = this.data()?.vehicle;
    return vehicle?.id ? [{ value: vehicle.id, label: vehicle.name ?? '' }] : [];
  }

  changeSelection(selected: { value: any; label: string }, type: 'driver' | 'vehicle') {
    this.changeEventEmitter.emit({ value: { id: selected.value, label: selected.label }, type });
  }

  getVehicleColor() {
    const vehicleId = this.data()?.vehicle?.id;
    return (vehicleId && this.vehicleColors[vehicleId]) || '#00FFFF';
  }

  getCardColor() {
    return this.phoneDrivers() ? this.getDriverColor() : this.getVehicleColor();
  }

  changeData(data: any, type: string) {
    const value = !data.options ? data.target.value : data.options?.[0];
    this.changeEventEmitter.emit({value, type});
  }

  closeEditMode(event: FocusEvent, type: 'times' | 'driver' | 'vehicle') {
    const container = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement;

    // Don't close if focus is moving to another element within the same container
    if (relatedTarget && container.contains(relatedTarget)) {
      return;
    }

    // Small delay to allow focus to settle
    setTimeout(() => {
      if (type === 'times') {
        this.modifyTimes = false;
      } else if (type === 'driver') {
        this.modifyDriver = false;
      } else if (type === 'vehicle') {
        this.modifyVehicle = false;
      }
    }, 100);
  }

  onCardClick(event: MouseEvent) {
    if (!this.multiselectEnabled()) return;
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT') return;
    this.checkDay.emit(this.id());
  }

  editTimes() {
    if (!this.multiselectEnabled()) {
      this.modifyTimes = true;
      // Auto-focus the first time input after rendering
      setTimeout(() => {
        const input = document.querySelector(`#times-${this.id()} input[type="time"]`) as HTMLInputElement;
        input?.focus();
        input?.showPicker?.();
      }, 50);
    }
  }

  editDriver() {
    if (!this.multiselectEnabled()) {
      this.modifyDriver = true;
      // Auto-open the select2 dropdown after rendering
      setTimeout(() => {
        const select2 = document.querySelector(`#driver-select-${this.id()} .select2-selection`) as HTMLElement;
        select2?.click();
      }, 50);
    }
  }

  editVehicle() {
    if (!this.multiselectEnabled()) {
      this.modifyVehicle = true;
      // Auto-open the select2 dropdown after rendering
      setTimeout(() => {
        const select2 = document.querySelector(`#vehicle-select-${this.id()} .select2-selection`) as HTMLElement;
        select2?.click();
      }, 50);
    }
  }

}
